/**
 * BetterAuth-backed AuthContext.
 *
 * Drop-in replacement for the Supabase-backed AuthContext.  Exposes the exact
 * same public shape (`useAuthContext()`) so every consumer (hooks, pages,
 * components) works without changes.
 *
 * Internally:
 *   1. Gets the BetterAuth session via `authClient.useSession()`.
 *   2. Fetches the full application user record (with `company_id`, `role`,
 *      `full_name`, etc.) from the Hono API.
 *   3. Provides signIn / signOut / createUserAndCompany methods.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { authClient } from '@/lib/auth-client';
import { setDemoRegion, clearDemoRegion } from '@/lib/demo/demo-region';
import { invalidateSessionCache } from '@/lib/api-client';
import { isPublicDemoRoute } from '@/lib/public-demo-routes';
import { User } from '@/types/database';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

// ---------------------------------------------------------------------------
// Context type — matches the original AuthContext exactly
// ---------------------------------------------------------------------------

interface AuthContextType {
  session: unknown;
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  createUserAndCompany: (
    email: string,
    fullName: string,
    companyName: string,
    persona?: string,
  ) => Promise<{ user: User; company: any } | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function BetterAuthProvider({ children }: { children: ReactNode }) {
  const { data: betterAuthSession, isPending } = authClient.useSession();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // -----------------------------------------------------------------------
  // Load the full application user (with company_id) from the Hono API.
  //
  // Skip entirely on public demo routes (Toyota / Nexteer / Katcon /
  // TREMEC / GDLS) — those pages do not require a session and the API
  // may be unreachable on dev machines (port 5000 collision with macOS
  // Control Center). Gap-close #3 (P0) — PRD-1.
  // -----------------------------------------------------------------------
  const loadUserData = useCallback(async () => {
    if (isPublicDemoRoute()) {
      setUser(null);
      setLoading(false);
      return;
    }
    if (!betterAuthSession?.user) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/companies/current-user`, {
        credentials: 'include',
      });

      if (!res.ok) {
        const isDemoMode = sessionStorage.getItem('demo_mode') === 'true';
        if (!isDemoMode && window.location.pathname !== '/onboarding') {
          navigate('/onboarding');
        }
        setLoading(false);
        return;
      }

      const data = await res.json();
      if (data) {
        setUser(data as User);
      }
    } catch (err) {
      console.error('BetterAuthProvider: Error loading user data:', err);
    } finally {
      setLoading(false);
    }
  }, [betterAuthSession?.user, navigate]);

  useEffect(() => {
    if (isPending) return;
    loadUserData();
  }, [isPending, loadUserData]);

  // -----------------------------------------------------------------------
  // Auth actions
  // -----------------------------------------------------------------------

  const signInWithGoogle = async () => {
    try {
      await authClient.signIn.social({
        provider: 'google',
        callbackURL: `${window.location.origin}/dashboard`,
      });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to sign in with Google');
    }
  };

  const signInWithPassword = async (
    email: string,
    password: string,
  ): Promise<boolean> => {
    try {
      const result = await authClient.signIn.email({ email, password });
      if (result.error) {
        toast.error(result.error.message || 'Invalid email or password');
        return false;
      }
      // BUG-56: the MX demo tenant must render in Spanish even when signed in
      // through the regular login form (not just the demo region picker, which
      // is the only place that set demo_region before). Tag the MX demo email
      // as the MX region + default the locale to Spanish, mirroring
      // DemoAccountSetup. Only sets language when the user hasn't already
      // chosen one (preserves an explicit Settings choice — see SettingsContext).
      if (email.trim().toLowerCase() === 'demo-mx@alisiofi.com') {
        // BUG-88: persists to localStorage too, so the region survives a new tab
        // the same way demo mode and the language already do.
        setDemoRegion('mx');
        try {
          const existing = JSON.parse(localStorage.getItem('app-settings') || '{}');
          if (!existing.language) {
            localStorage.setItem('app-settings', JSON.stringify({ ...existing, language: 'es' }));
          }
        } catch {
          localStorage.setItem('app-settings', JSON.stringify({ language: 'es' }));
        }
      }
      invalidateSessionCache();
      return true;
    } catch (err: any) {
      toast.error(err?.message || 'Sign in failed');
      return false;
    }
  };

  const signOut = async () => {
    try {
      sessionStorage.removeItem('demo_mode');
      sessionStorage.removeItem('demo_persona');
      sessionStorage.removeItem('demo_email');
      sessionStorage.removeItem('demo_name');
      sessionStorage.removeItem('demo_company');
      clearDemoRegion();
      localStorage.removeItem('alisio-demo-mode');

      await authClient.signOut();
      invalidateSessionCache();

      setUser(null);
      navigate('/login');
    } catch (err: any) {
      toast.error(err?.message || 'Sign out failed');
    }
  };

  const createUserAndCompany = async (
    email: string,
    fullName: string,
    companyName: string,
    persona?: string,
  ): Promise<{ user: User; company: any } | null> => {
    try {
      // For demo accounts, create the BetterAuth user first
      if (
        persona &&
        (email.includes('demo') || email.includes('alisiofi.com'))
      ) {
        const demoPassword = `demo-${Math.random().toString(36).substring(2, 15)}`;
        const signUpResult = await authClient.signUp.email({
          email,
          password: demoPassword,
          name: fullName,
        });
        if (signUpResult.error) {
          toast.error(
            signUpResult.error.message || 'Failed to create demo account',
          );
          return null;
        }
      }

      // Call the API to create the user + company record
      const res = await fetch(`${API_URL}/api/companies/create-user-and-company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email,
          fullName,
          companyName,
          persona: persona || 'controller',
        }),
      });

      if (!res.ok) {
        const errorData = await res
          .json()
          .catch(() => ({ error: 'Unknown error' }));
        const errorMsg = errorData.error || 'Failed to create account';

        if (
          errorMsg.includes('already exists') ||
          errorMsg.includes('already have an account')
        ) {
          toast.error(errorMsg);
          setTimeout(() => navigate('/dashboard'), 1500);
        } else {
          toast.error(errorMsg);
        }
        return null;
      }

      const result = await res.json();

      if (result.user) {
        setUser(result.user as User);
        toast.success(
          persona
            ? 'Demo account created successfully!'
            : 'Account created successfully!',
        );
        setTimeout(() => navigate('/dashboard'), 500);
        return result as { user: User; company: any };
      }

      toast.error('Failed to create account - no data returned');
      return null;
    } catch (error: any) {
      console.error(
        'BetterAuthProvider: Exception in createUserAndCompany:',
        error,
      );
      toast.error('An unexpected error occurred');
      return null;
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const isDemoMode = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('demo_mode') === 'true';
  const isReady = !isPending && !loading;

  // Show loading spinner only during initial load — but never block demo mode
  if (!isReady && !user && !isDemoMode) {
    return (
      <AuthContext.Provider
        value={{
          session: betterAuthSession,
          user: null,
          loading: true,
          signInWithGoogle,
          signInWithPassword,
          signOut,
          createUserAndCompany,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        session: betterAuthSession,
        user,
        loading: loading || isPending,
        signInWithGoogle,
        signInWithPassword,
        signOut,
        createUserAndCompany,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Consumers — same names as the old AuthContext for drop-in compatibility
// ---------------------------------------------------------------------------

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within a BetterAuthProvider');
  }
  return context;
}

/** Alias for backwards compatibility with BetterAuthContext imports */
export const useBetterAuth = useAuthContext;

/** Re-export the provider under the old name for gradual migration */
export const AuthProvider = BetterAuthProvider;
